import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChatCircle, Trash, PaperPlaneRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const REACTIONS = ["👍", "❤️", "🎉", "🙌"];

function renderText(text = "") {
  return text.split(/(@[A-Za-z][\w.\-]{1,40})/g).map((part, i) =>
    part.startsWith("@") ? <span key={i} className="font-medium text-secondary">{part}</span> : part);
}

function MentionInput({ pid, onPost }) {
  const [text, setText] = useState("");
  const [mentionIds, setMentionIds] = useState([]);
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const taRef = useRef(null);

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    const caret = e.target.selectionStart;
    const m = v.slice(0, caret).match(/@(\w{0,40})$/);
    if (m) {
      api.get(`/projects/${pid}/mentionables`, { params: { q: m[1] } })
        .then((r) => { setSugs(r.data.users || []); setOpen((r.data.users || []).length > 0); })
        .catch(() => setOpen(false));
    } else setOpen(false);
  };
  const pick = (u) => {
    const ta = taRef.current;
    const caret = ta ? ta.selectionStart : text.length;
    const before = text.slice(0, caret).replace(/@(\w{0,40})$/, `@${(u.name || "").split(" ")[0]} `);
    setText(before + text.slice(caret));
    setMentionIds((ids) => (ids.includes(u.user_id) ? ids : [...ids, u.user_id]));
    setOpen(false);
    if (ta) ta.focus();
  };
  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    await onPost(text, mentionIds);
    setText(""); setMentionIds([]); setOpen(false); setBusy(false);
  };

  return (
    <div className="relative mt-3">
      <div className="flex gap-2">
        <Textarea ref={taRef} rows={2} value={text} onChange={onChange}
          placeholder="Share an idea, offer help or ask a question… use @ to mention someone"
          data-testid="comment-input" />
        <Button onClick={submit} disabled={busy} className="gap-1.5" data-testid="comment-submit">
          <PaperPlaneRight size={15} /> Post
        </Button>
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-72 overflow-hidden rounded-xl border border-border/60 bg-white shadow-lg" data-testid="mention-dropdown">
          {sugs.map((u) => (
            <button key={u.user_id} onClick={() => pick(u)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50" data-testid={`mention-option-${u.user_id}`}>
              {u.avatar_file_id
                ? <img src={fileUrl(u.avatar_file_id)} alt="" className="h-7 w-7 rounded-full object-cover" />
                : <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary/15 text-[11px] font-semibold text-secondary">{(u.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}</span>}
              <span className="font-medium">{u.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
}

function CommentRow({ c, isReply, pid, canModerate, myId, onChange }) {
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState("");
  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/projects/${pid}/comments`, { text, parent_id: c.id });
      setText("");
      setReplying(false);
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  const del = async () => {
    try {
      await api.delete(`/projects/${pid}/comments/${c.id}`);
      onChange();
    } catch {
      toast.error("Could not delete");
    }
  };
  const canDelete = canModerate || c.user_id === myId;
  const react = async (emoji) => {
    try { await api.post(`/projects/${pid}/comments/${c.id}/react`, { emoji }); onChange(); }
    catch { toast.error("Could not react"); }
  };
  const counts = c.reaction_counts || {};
  const mine = c.my_reactions || [];
  return (
    <div className="flex gap-3" data-testid={`comment-${c.id}`}>
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary/15 text-xs font-semibold text-secondary">
        {initials(c.user_name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="rounded-2xl bg-muted/50 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">{c.user_name}</span>
            {canDelete ? (
              <button onClick={del} className="text-muted-foreground hover:text-red-600" title="Delete">
                <Trash size={13} />
              </button>
            ) : null}
          </div>
          <p className="whitespace-pre-wrap text-sm text-foreground/90">{renderText(c.text)}</p>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 pl-1 text-xs text-muted-foreground">
          <span>{(c.created_at || "").slice(0, 10)}</span>
          {isReply ? null : (
            <button className="font-medium hover:text-secondary" onClick={() => setReplying((r) => !r)}>
              Reply
            </button>
          )}
          <span className="flex items-center gap-1" data-testid={`reactions-${c.id}`}>
            {REACTIONS.map((emo) => (
              <button key={emo} onClick={() => react(emo)}
                className={`rounded-full px-1.5 py-0.5 transition-colors hover:bg-secondary/10 ${mine.includes(emo) ? "bg-secondary/15 ring-1 ring-secondary/40" : ""}`}
                data-testid={`react-${emo}-${c.id}`}>
                {emo}{counts[emo] ? <span className="ml-0.5">{counts[emo]}</span> : null}
              </button>
            ))}
          </span>
        </div>
        {replying ? (
          <div className="mt-2 flex gap-2">
            <Textarea
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write a reply\u2026"
              data-testid={`reply-input-${c.id}`}
            />
            <Button size="sm" onClick={send}>
              <PaperPlaneRight size={14} />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function ProjectDiscussion({ pid, canModerate = false }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const load = useCallback(() => {
    api.get(`/projects/${pid}/comments`).then((r) => setComments(r.data)).catch(() => {});
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const postComment = async (text, mentionIds) => {
    if (!text.trim()) return;
    try {
      await api.post(`/projects/${pid}/comments`, { text, mention_ids: mentionIds });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const top = comments.filter((c) => !c.parent_id);
  const repliesOf = (id) => comments.filter((c) => c.parent_id === id);

  return (
    <section className="mt-6 rounded-2xl border border-border/60 bg-white p-5" data-testid="project-discussion">
      <div className="flex items-center gap-2">
        <ChatCircle size={20} weight="duotone" className="text-secondary" />
        <h2 className="font-heading text-xl font-medium">Discussion</h2>
        <span className="text-sm text-muted-foreground">({top.length})</span>
      </div>
      {user ? (
        <MentionInput pid={pid} onPost={postComment} />
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">Log in to join the discussion.</p>
      )}
      <div className="mt-5 space-y-4">
        {top.length === 0 ? (
          <p className="text-sm text-muted-foreground">No comments yet — start the conversation.</p>
        ) : null}
        {top.map((c) => {
          const replies = repliesOf(c.id);
          return (
            <div key={c.id} className="space-y-3">
              <CommentRow c={c} isReply={false} pid={pid} canModerate={canModerate} myId={user?.id} onChange={load} />
              {replies.length > 0 ? (
                <div className="ml-12 space-y-3 border-l-2 border-border/50 pl-3">
                  {replies.map((r) => (
                    <CommentRow key={r.id} c={r} isReply pid={pid} canModerate={canModerate} myId={user?.id} onChange={load} />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
